import React, { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "typedefs";
import { useSelector } from "app/utils/hooks";
import AcademyWizardLayout from "app/components/Layouts/AcademyWizardLayout";
import { Loading } from "app/components/Wrappers";
import { states } from "app/components/StripeForm/states";
import {
  getGeneralFacility,
  parseGeneralFacility,
  saveGeneralFacility,
  ICoreFacilityV1,
} from "app/routes/AcademyWizard/api";
import SectionHeading from "app/routes/AcademyWizard/_components/SectionHeading";
import WizardTextField from "app/routes/AcademyWizard/_components/WizardTextField";
import WizardSelectField from "app/routes/AcademyWizard/_components/WizardSelectField";
import WizardCountryField from "app/routes/AcademyWizard/_components/WizardCountryField";
import {
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  FACILITY_NAME_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  validateAddress,
  validateCity,
  validateFacilityName,
  validatePostalCodeField,
  validateState,
} from "app/routes/AcademyWizard/validation";
import styles from "./styles.module.scss";

const stateOptions = states.map(
  (state: { name: string; abbreviation: string }) => ({
    value: state.abbreviation,
    label: state.name,
  }),
);

// Ties the visible label to the input WizardCountryField renders internally.
const COUNTRY_FIELD_ID = "academy-wizard-country";

const AcademyWizardGeneral = () => {
  const { t } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const [facilityName, setFacilityName] = useState("");
  const [country, setCountry] = useState("US");
  const [usState, setUsState] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [touched, setTouched] = useState({
    facilityName: false,
    address: false,
    state: false,
    city: false,
    zip: false,
  });
  const [loading, setLoading] = useState(true);
  // The fetched record is kept whole so the PUT can merge edited fields over
  // untouched ones (street2, timezone) instead of blanking them.
  const facilityRef = useRef<ICoreFacilityV1 | null>(null);
  const isMounted = useRef(true);
  const fetched = useRef(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // The record to edit is the coach's scheduling facility, which fetchSelf
  // loads after the account itself (a hard reload of this step races the
  // two). It only ever loads for coaches with the scheduling feature, so any
  // other account is resolved at once and falls back to the first facility.
  const hasScheduling = !!self && (self.features || []).includes("scheduling");
  const schedulingResolved =
    !!self && (self.scheduling !== undefined || !hasScheduling);
  const currentFacilityId = self?.scheduling?.facility?.id ?? null;
  // A scheduling coach always has a scheduling facility, so a resolved
  // request without one is the failed fetch (fetchSelf stores an empty
  // facility and never retries), not an account to fall back for.
  const schedulingFailed =
    hasScheduling && schedulingResolved && currentFacilityId === null;

  useEffect(() => {
    if (!schedulingResolved || fetched.current) {
      return;
    }
    fetched.current = true;
    if (schedulingFailed) {
      // Nothing safe to edit: the fallback would open another facility's
      // record. The step stays empty and Next reports the error again.
      toast.error(t("Something went wrong."));
      setLoading(false);
      return;
    }
    getGeneralFacility(currentFacilityId)
      .then((facility) => {
        if (!isMounted.current || !facility) {
          return;
        }
        facilityRef.current = facility;
        const form = parseGeneralFacility(facility);
        setFacilityName(form.facilityName);
        setCountry(form.country);
        setUsState(form.usState);
        setAddress(form.address);
        setCity(form.city);
        setZip(form.zip);
      })
      .catch((error) => {
        console.error("Error loading facility:", error);
        if (isMounted.current) {
          toast.error(t("Something went wrong."));
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setLoading(false);
        }
      });
  }, [schedulingResolved, schedulingFailed, currentFacilityId]);

  const isUS = country === "US";

  const touch = (field: keyof typeof touched) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // Derived rather than stored, so switching country re-checks the Zip against
  // the new country's format — and drops the State error when it stops being
  // required — for free: no effect, no stale error.
  const facilityNameError = touched.facilityName
    ? validateFacilityName(facilityName)
    : null;
  const addressError = touched.address ? validateAddress(address) : null;
  const stateError = touched.state ? validateState(usState, country) : null;
  const cityError = touched.city ? validateCity(city) : null;
  const zipError = touched.zip ? validatePostalCodeField(zip, country) : null;

  const onCountrySelect = (code: string) => {
    setCountry(code);
    if (code !== "US") {
      setUsState("");
    }
  };

  // Reveals errors on fields the user never blurred, then saves the same way
  // /manage_facility's Edit Facility modal does.
  const onBeforeNext = () => {
    setTouched({
      facilityName: true,
      address: true,
      state: true,
      city: true,
      zip: true,
    });
    const valid =
      !validateFacilityName(facilityName) &&
      !validateAddress(address) &&
      !validateState(usState, country) &&
      !validateCity(city) &&
      !validatePostalCodeField(zip, country);
    if (!valid) {
      return Promise.resolve(false);
    }
    if (!facilityRef.current) {
      // No fetched record → nothing to merge the PUT onto. Thrown (not
      // `false`) so the layout's catch shows its error toast; the fields are
      // valid, so there is nothing on screen explaining a dead Next button.
      throw new Error("No facility record to save the General step into");
    }
    return saveGeneralFacility(facilityRef.current, {
      facilityName,
      country,
      usState,
      address,
      city,
      zip,
    }).then(() => true);
  };

  // Field counts for the academy_wizard_step_completed event: completed =
  // non-empty now, prefilled = non-empty as fetched, edited = differs from the
  // fetched value.
  const collectStepStats = () => {
    const values = [facilityName, country, address, usState, city, zip];
    const initial = facilityRef.current
      ? parseGeneralFacility(facilityRef.current)
      : null;
    const initialValues = initial
      ? [
          initial.facilityName,
          initial.country,
          initial.address,
          initial.usState,
          initial.city,
          initial.zip,
        ]
      : [];
    return {
      completed: values.filter((value) => value && value.trim()).length,
      prefilled: initialValues.filter((value) => value && value.trim()).length,
      edited: initial
        ? values.filter((value, index) => value !== initialValues[index]).length
        : 0,
    };
  };

  return (
    <AcademyWizardLayout
      stepPath="/academy/wizard/general"
      title={t("Tell us about your facility")}
      onBeforeNext={onBeforeNext}
      loading={loading}
      collectStepStats={collectStepStats}
    >
      {loading ? (
        <Loading isLoading loadType="spinner" />
      ) : (
        <>
          <div className={styles.section}>
            <SectionHeading
              title={t("Facility Name")}
              subtitle={t(
                "This appears on your public landing page and all booking confirmations. If you're a solo coach, you can put the name of your business or the location where you give lessons.",
              )}
            />
            <WizardTextField
              label={t("Facility Name *")}
              required
              placeholder={t("Central Golf Club")}
              value={facilityName}
              maxLength={FACILITY_NAME_MAX_LENGTH}
              error={facilityNameError ? t(facilityNameError) : undefined}
              onChange={setFacilityName}
              onBlur={touch("facilityName")}
            />
          </div>
          <div className={styles.section}>
            <SectionHeading
              title={t("Where is your facility located?")}
              subtitle={t("This will be shown on your public page.")}
            />
            <div className={styles.country_field}>
              <label
                className={styles.country_label}
                htmlFor={COUNTRY_FIELD_ID}
              >
                {t("Country *")}
              </label>
              <WizardCountryField
                id={COUNTRY_FIELD_ID}
                selected={country}
                onSelect={onCountrySelect}
                required
              />
            </div>
            <WizardTextField
              label={t("Street Address")}
              required
              placeholder={t("Street Address")}
              value={address}
              maxLength={ADDRESS_MAX_LENGTH}
              error={addressError ? t(addressError) : undefined}
              onChange={setAddress}
              onBlur={touch("address")}
            />
            <div className={styles.row}>
              {isUS && (
                <div className={styles.row_item}>
                  <WizardSelectField
                    label={t("State")}
                    required
                    placeholder={t("Select state")}
                    value={usState}
                    options={stateOptions}
                    error={stateError ? t(stateError) : undefined}
                    onChange={setUsState}
                    onBlur={touch("state")}
                  />
                </div>
              )}
              <div className={styles.row_item}>
                <WizardTextField
                  label={t("City")}
                  required
                  placeholder={t("City")}
                  value={city}
                  maxLength={CITY_MAX_LENGTH}
                  error={cityError ? t(cityError) : undefined}
                  onChange={setCity}
                  onBlur={touch("city")}
                />
              </div>
              {/* Without the State column, City and Zip split the row 50/50. */}
              <div className={isUS ? styles.zip : styles.row_item}>
                <WizardTextField
                  // Labeled "Zip" in every country by design, even though outside
                  // the US the value is validated as that country's postal code.
                  label={t("Zip")}
                  required
                  // Teaches the expected US format without a long error message.
                  placeholder={isUS ? "90210" : t("Zip")}
                  value={zip}
                  maxLength={POSTAL_CODE_MAX_LENGTH}
                  error={zipError ? t(zipError) : undefined}
                  onChange={setZip}
                  onBlur={touch("zip")}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </AcademyWizardLayout>
  );
};

export default memo(AcademyWizardGeneral);
